import { Tooltip } from '@pms-digital/design-system';
import type { ReactElement } from 'react';

/**
 * The bubble that carries a persona's PROSE: the direction of an involvement
 * cran, and the text of a tone (issue #104; refonte modulaire §10.1).
 *
 * ## One component, mounted twice, and that is the decision
 *
 * A tone tag and a point of the slider carry the same THING - a piece of
 * writing one reads on hover to decide what sits under the pointer, on the same
 * screen, in the same column of the same form. Two widths and two text sizes
 * for that would be a distinction nobody could explain, so the two the mockups
 * draw (320 px over the cran, 300 px over the tag) are settled HERE at one
 * value rather than at each call site.
 *
 * ## The mockups are stale on the COLOUR, and only on the colour
 *
 * `docs/specs/maquettes/personas/*.dc.html` draw both of these bubbles dark -
 * a near-black ground, white ink. The design system decides against them: white
 * is the one ground of the PMS brand, even for a transitory component. Nothing
 * here paints a ground at all: the package `Tooltip` already IS the white
 * bubble. The mockups are read for their sizes.
 *
 * ## The RICH variant, for its width
 *
 * The package `Tooltip` sizes its plain bubble for a LABEL - 240 px - which
 * this is not, and it takes no width of its own. Its rich variant (`title`)
 * widens the bubble to 280 px and sets the body in a readable 13 px, so this
 * is the variant mounted: the title is the NAME of what is under the pointer
 * (the cran, the tone), the body is its prose. 280 and not the 320 the mockup
 * draws: the package has no wider bubble (a gap of the package, not a choice here).
 *
 * ## It WRAPS, and nothing about it truncates
 *
 * No `truncate`, no ellipsis, no fade - « wraps before truncates ». The schema
 * caps both texts at 2000 characters (`PERSONA_TONE_TEXT_MAX`,
 * `INVOLVEMENT_LEVEL_DIRECTION_MAX`), which is taller than a laptop window at
 * this width. The package bubble has no height cap of its own (the bubble this
 * one replaced stopped at the window and scrolled): Radix keeps it inside the
 * window by flipping and shifting, and a text that long overflows it: a gap
 * of the package as well.
 *
 * ## The `sr-only` sibling STAYS, and it is what describes the control
 *
 * Both call sites point at an `sr-only` copy of this very sentence with
 * `aria-describedby`, and it STAYS: it is what answers the keyboard and the
 * touch screen, where the text has to exist without a hover. The package
 * `Tooltip` has no `describes={false}`: while open, Radix hands its trigger an
 * `aria-describedby` of its own. On the tone tag the tag's own attribute wins
 * the merge (the child's props override the trigger's), so the sentence is
 * still read once; on a cran the trigger is the LABEL, which nothing focuses,
 * and the radio inside keeps pointing at its one paragraph.
 */

/**
 * The line breaks the operator typed, and nothing else.
 *
 * `whitespace-pre-line` is not decoration: a tone's text is entered in a
 * TEXTAREA, and dropping its newlines would silently reflow somebody's
 * paragraphs into one block. It still wraps, so a long line is still folded
 * rather than cut. A `span`, because the package sets the body in one.
 */
const PROSE = 'block whitespace-pre-line';

export interface PersonaProseTooltipProps {
  /** The name of what sits under the pointer: the cran, the tone. The bubble's title. */
  readonly title: string;
  /**
   * The sentence itself. Never empty: both schemas bound it at `min(1)`, which
   * is why there is no « no bubble » branch here.
   */
  readonly text: string;
  /**
   * The control it sits over - ONE element that accepts a `ref` and pointer
   * handlers, which is the whole contract Radix imposes on a trigger (it is
   * mounted `asChild`, so nothing of the layout moves).
   */
  readonly children: ReactElement;
}

export function PersonaProseTooltip({ title, text, children }: PersonaProseTooltipProps) {
  return (
    <Tooltip title={title} content={<span className={PROSE}>{text}</span>}>
      {children}
    </Tooltip>
  );
}
